import { getOperationName } from '@apollo/client/utilities';
import { Descriptions, Modal, Skeleton, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import Breakpoint from '../../constants/Breakpoint';
import paths from '../../paths.json';
import { formatDateTime } from '../../utils/format';
import { NotificationsDocument as NotificationBadgeDocument } from '../NotificationBadge/Notifications.generated';
import { NotificationsDocument as NotificationListDocument } from '../NotificationList/Notifications.generated';
import NotificationSeverity from '../NotificationSeverity';
import NotificationTag from '../NotificationTag';

import { useNotificationQuery } from './Notification.generated';
import styles from './styles.module.less';
import { useUpdateNotificationMutation } from './UpdateNotification.generated';

const NotificationModal = () => {
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch<{ 0: string; notificationId?: string }>([
    `*/:notificationId([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})`,
  ]);

  const open = useMemo(
    () => Boolean(match && !location.pathname.includes('postback-links')),
    [location.pathname, match],
  );

  const id = useMemo(
    () => match?.params?.notificationId,
    [match?.params?.notificationId],
  );

  const [updateNotification] = useUpdateNotificationMutation({
    refetchQueries: [
      getOperationName(NotificationListDocument) as string,
      getOperationName(NotificationBadgeDocument) as string,
    ],
  });

  const { data, loading } = useNotificationQuery({
    onCompleted: (data) => {
      void updateNotification({
        variables: { id: data.notification.id, read: true },
      });
    },
    skip: !id || location.pathname.includes('postback-links'),
    variables: {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      id,
    },
  });

  const notification = useMemo(() => data?.notification, [data?.notification]);

  const relations = useMemo(
    () => notification?.relatedEntities?.[0],
    [notification?.relatedEntities],
  );

  const onCancel = useCallback(() => {
    history.push({ ...location, pathname: match?.params[0] || '/' });
  }, [history, location, match?.params]);

  useEffect(() => {
    if (open && !notification && !loading) {
      onCancel();
    }
  }, [loading, notification, onCancel, open]);

  return (
    <Modal
      key="test"
      cancelText="Close"
      className={styles.modal}
      destroyOnClose
      okButtonProps={{
        hidden: true,
      }}
      onCancel={onCancel}
      open={open}
      title={
        notification?.title ? (
          <Typography.Text strong>{notification.title}</Typography.Text>
        ) : (
          <Skeleton loading paragraph={false} />
        )
      }
      width={Breakpoint.MD_MIN}
      zIndex={1050}
    >
      {notification ? (
        <>
          <Typography.Paragraph
            ellipsis={{ expandable: true, rows: 5 }}
            style={{
              marginBottom: 0,
              padding: '16px 24px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {notification.message}
          </Typography.Paragraph>
          <Descriptions
            bordered
            className={styles.descriptions}
            column={1}
            size="small"
          >
            <Descriptions.Item label="Time">
              {formatDateTime(notification.time as string)}
            </Descriptions.Item>
            <Descriptions.Item label="Severity">
              <NotificationSeverity severity={notification.severity} />
            </Descriptions.Item>
            <Descriptions.Item label="Type">
              <NotificationTag tag={notification.tag} />
            </Descriptions.Item>
            {relations?.offer && (
              <Descriptions.Item label="Offer">
                <Link to={`${paths.offers}/${relations.offer?.id}`}>
                  {relations.offer?.name}
                </Link>
              </Descriptions.Item>
            )}
          </Descriptions>
        </>
      ) : (
        <Skeleton active />
      )}
    </Modal>
  );
};

NotificationModal.displayName = 'NotificationModal';

export default NotificationModal;
